import { createFileRoute } from '@tanstack/react-router';
import { Receipt } from '@maia/ui';
import { getProductsOptions } from '../../../../features/quotation/products/queryOptions';
import { getCompositionsOptions } from '../../../../features/quotation/compositions/queryOptions';
import { getQuotationOptions } from '../../../../features/quotation/quotation/queryOptions';
import { getDocumentsOptions } from '../../../../features/quotation/documents/queryOptions';
import { useMemo } from 'react';
import { sumBy } from 'es-toolkit';
import { DocumentCollectionProductDto } from '@maia/shared';

export const Route = createFileRoute(
  '/_authenticated/quotations/$guid/_layout/',
)({
  component: Page,
  loader: async ({ context: { queryClient }, params: { guid } }) => {
    const quotation = await queryClient.ensureQueryData(
      getQuotationOptions(guid),
    );

    const documents = await queryClient.ensureQueryData(
      getDocumentsOptions(guid),
    );

    const products = await queryClient.ensureQueryData(
      getProductsOptions(guid),
    );

    const compositions = await queryClient.ensureQueryData(
      getCompositionsOptions(guid),
    );

    return { products, quotation, documents, compositions };
  },
});

function Page() {
  const data = Route.useLoaderData();

  const totalPrice = useMemo(() => {
    const allPriceObjects = data.products
      .map((product) => [
        product.price,
        ...(product?.addOns ?? [])
          .filter((addon) => addon.answer.value === 'J')
          .map((addon) => addon.price),
      ])
      .flatMap((price) => price);

    return {
      tax: sumBy(allPriceObjects, (product) => product?.tax ?? 0),
      price: sumBy(allPriceObjects, (product) => product?.premiumAfterTax ?? 0),
    };
  }, [data.products]);

  const flattenProducts = useMemo(() => {
    return data.products.flatMap((product) => {
      let foundDocumentCollection: DocumentCollectionProductDto | undefined;

      const flattenedDocumentCollection = data.documents.flatMap(
        (documentCollection) => documentCollection.products,
      );

      if (product.composition) {
        foundDocumentCollection = flattenedDocumentCollection.find(
          (collection) => collection.composition?.guid === product?.composition,
        );
      } else {
        foundDocumentCollection = flattenedDocumentCollection.find(
          (collection) => collection.name === product.name,
        );
      }

      let productName = product.name;

      if (product.isComposition && product.composition) {
        const foundComposition = data.compositions.find(
          (comp) => comp.guid === product.composition,
        );
        if (foundComposition) {
          productName += ` ${foundComposition.title}`;
        }
      }

      const baseProduct = {
        product: productName,
        icon: product.icon,
        price: product.price.premiumAfterTax,
        specs: product.specs.map((spec) => spec.value),
        documents: foundDocumentCollection?.documents ?? [],
        clauses: foundDocumentCollection?.clauses ?? [],
      };

      const addOns =
        product.addOns
          ?.filter((addon) => addon.answer.value === 'J')
          .map((addOn) => {
            const foundAddon = foundDocumentCollection?.addons.find(
              (_addon) => _addon.code === addOn.guid,
            );

            return {
              product: addOn.phrasing,
              icon: addOn.icon,
              price: addOn.price?.premiumAfterTax ?? 0,
              specs: [],
              documents: foundAddon?.documents ?? [],
              clauses: foundAddon?.clauses ?? [],
            };
          }) ?? [];

      return [baseProduct, ...addOns];
    });
  }, [data.products, data.compositions, data.documents]);

  return (
    <div className="col-span-full 2xl:col-span-6">
      <Receipt
        totalPrice={totalPrice.price}
        tax={totalPrice.tax}
        items={flattenProducts}
        billingUnit={data.quotation.customer.billingUnit}
      />
    </div>
  );
}
